import React, { useState, useEffect, useRef, useReducer, useCallback } from 'react';
import Icon from '../shared/Icon';
import Chip from '../shared/Chip';
import { ABSENT, formatTimestamp, toIsoInstant } from '../../../shared/datetime/timestamps';
import './NotificationsPopover.css';

// Server notifications carry {id, title, message, read, metadata, created_at}; the
// variant/chip live in metadata (backend may not populate them yet, so default to
// info / no chip). Vocabulary maps onto the four ResUI statuses; "warning" keeps the
// prototype's class name but is the "Alert" status.
const normalizeVariant = (n) => {
  const raw = String(n.metadata?.variant || n.metadata?.severity || n.metadata?.level || n.type || '').toLowerCase();
  if (raw === 'success' || raw === 'ok') return 'success';
  if (raw === 'error' || raw === 'failure' || raw === 'failed') return 'error';
  if (['warning', 'warn', 'alert', 'partial', 'delayed'].includes(raw)) return 'warning';
  return 'info';
};

const chipFor = (n, variant) => {
  const chip = n.metadata?.chip;
  return chip && (variant === 'warning' || variant === 'error') ? String(chip) : '';
};

// Notification severity -> Figma chip variant (PRD-1361).
const CHIP_VARIANT = { warning: 'alert', error: 'error' };

// Map a server notification into the popover's display shape.
const toItem = (n) => {
  const variant = normalizeVariant(n);
  return {
    id: n.id,
    title: n.title,
    text: n.message,
    at: n.created_at || null,
    unread: !n.read,
    variant,
    chip: chipFor(n, variant),
  };
};

// The row is a button, so its time cannot be the focusable shared <Timestamp> (a control
// inside a control). It says the same things another way: the short form on the row, the
// full time with zone on hover, and to screen readers as the row button's description.
// The panel's own 30s tick keeps the relative label current.
// Whether a notification has a time to show. One test for the row's description and the
// text it points at, so a row never names a description that was not rendered.
const hasTime = (at) => formatTimestamp(at, 'short') !== ABSENT;

function NotificationTime({ id, at }) {
  if (!hasTime(at)) return <span className="notif-time">{ABSENT}</span>;
  const short = formatTimestamp(at, 'short');
  const full = formatTimestamp(at, 'static');
  return (
    <>
      <time className="notif-time" dateTime={toIsoInstant(at)} title={full}>{short}</time>
      <span id={id} hidden>{full}</span>
    </>
  );
}

/**
 * NotificationsPopover — the ResUI-Bridge notifications panel (PRD-1417).
 *
 * Presentational: it holds the display state (read/unread, active tab) seeded
 * from the `notifications` prop and calls back to persist. Opening a row or the
 * toggle's unread→read direction calls onMarkRead (the real API). The toggle's
 * read→unread direction is local-only — there is no mark-unread endpoint yet;
 * a reopen reseeds from the server. Backend follow-up tracked separately.
 */
const NotificationsPopover = ({ open, notifications = [], onClose, onMarkRead, onMarkAllRead }) => {
  // Initialise from props so the first paint already has rows (no empty flash).
  const [items, setItems] = useState(() => (notifications || []).map(toItem));
  const [filter, setFilter] = useState('all');
  const [, tick] = useReducer((x) => x + 1, 0); // 30s relative-time refresh
  const listRef = useRef(null);
  const allTabRef = useRef(null);
  const unreadTabRef = useRef(null);

  // Reseed display state whenever the server list changes (e.g. on reopen).
  useEffect(() => {
    setItems((notifications || []).map(toItem));
  }, [notifications]);

  // Relative labels must not go stale while the panel is open.
  useEffect(() => {
    if (!open) return undefined;
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [open]);

  // Focus the active tab on open; Escape closes and returns focus to the trigger.
  useEffect(() => {
    if (!open) return undefined;
    (filter === 'unread' ? unreadTabRef : allTabRef).current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const unreadCount = items.filter((n) => n.unread).length;

  const handleRowOpen = useCallback((n) => {
    if (!n.unread) return; // opening a read row does nothing here (destination out of scope)
    setItems((prev) => prev.map((i) => (i.id === n.id ? { ...i, unread: false } : i)));
    onMarkRead?.(n.id);
  }, [onMarkRead]);

  const handleToggle = useCallback((n) => {
    const nowUnread = !n.unread;
    setItems((prev) => prev.map((i) => (i.id === n.id ? { ...i, unread: nowUnread } : i)));
    if (!nowUnread) onMarkRead?.(n.id); // only the read direction persists
  }, [onMarkRead]);

  const handleMarkAll = useCallback(() => {
    setItems((prev) => prev.map((i) => ({ ...i, unread: false })));
    onMarkAllRead?.();
  }, [onMarkAllRead]);

  const selectTab = (next) => {
    setFilter(next);
    if (listRef.current) listRef.current.scrollTop = 0;
  };
  const onTabKey = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const next = filter === 'all' ? 'unread' : 'all';
    selectTab(next);
    (next === 'unread' ? unreadTabRef : allTabRef).current?.focus();
  };

  if (!open) return null;

  const shown = items.filter((n) => filter === 'all' || n.unread);
  const unreadLabel = unreadCount ? `Unread (${unreadCount})` : 'Unread';

  return (
    <div className="notif-panel" role="dialog" aria-labelledby="notifTitle">
      <div className="notif-head">
        <div className="notif-head-row">
          <h2 className="notif-title" id="notifTitle">Notifications</h2>
          <button
            type="button"
            className="notif-mark-all"
            onClick={handleMarkAll}
            disabled={unreadCount === 0}
          >
            Mark all as read
          </button>
        </div>
        <div className="notif-tabs" role="tablist" aria-label="Filter notifications">
          <button
            type="button"
            ref={allTabRef}
            className="notif-tab"
            role="tab"
            id="notifTabAll"
            data-label="All"
            aria-selected={filter === 'all'}
            tabIndex={filter === 'all' ? 0 : -1}
            onClick={() => selectTab('all')}
            onKeyDown={onTabKey}
          >
            All
          </button>
          <button
            type="button"
            ref={unreadTabRef}
            className="notif-tab"
            role="tab"
            id="notifTabUnread"
            data-label={unreadLabel}
            aria-selected={filter === 'unread'}
            tabIndex={filter === 'unread' ? 0 : -1}
            onClick={() => selectTab('unread')}
            onKeyDown={onTabKey}
          >
            {unreadLabel}
          </button>
        </div>
      </div>
      <ul
        className="notif-list"
        ref={listRef}
        role="tabpanel"
        aria-labelledby={filter === 'unread' ? 'notifTabUnread' : 'notifTabAll'}
      >
        {shown.length === 0 ? (
          <li className="notif-empty">No unread notifications.</li>
        ) : (
          shown.map((n) => {
            const tip = n.unread ? 'Mark as read' : 'Mark as unread';
            return (
              <li key={n.id} className={`notif notif--${n.variant}${n.unread ? ' is-unread' : ''}`}>
                <button
                  type="button"
                  className="notif-main"
                  onClick={() => handleRowOpen(n)}
                  aria-describedby={hasTime(n.at) ? `notif-${n.id}-time` : undefined}
                >
                  <span className="notif-body">
                    {n.unread && <span className="sr-only">Unread. </span>}
                    <span className="notif-name">{n.title}</span>
                    <span className="notif-text" title={n.text}>{n.text}</span>
                    <span className="notif-meta">
                      {n.chip && CHIP_VARIANT[n.variant] && (
                        <Chip variant={CHIP_VARIANT[n.variant]} className="notif-chip">{n.chip}</Chip>
                      )}
                      <NotificationTime id={`notif-${n.id}-time`} at={n.at} />
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="notif-toggle"
                  data-tip={tip}
                  aria-label={tip}
                  onClick={() => handleToggle(n)}
                >
                  <Icon name={n.unread ? 'envelope-simple' : 'envelope-simple-open'} size={20} className="icon" />
                </button>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
};

export default NotificationsPopover;
